// ----------------------------------------------------------------------
// Which campus this session acts at.
//
// The answer is persisted to localStorage and utils/axios injects it as
// `campus_id` on every request, so it decides what a member sees on check-in,
// the room list, the dashboard and everything else campus-scoped.
//
// For a member the SERVER already answers the same question:
// CompanyUserSettings.active_campus, resolved by CustomUser.active_campus()
// and returned on the user payload as `activeCampus`. The two used to be
// chosen independently and they drift. A member whose account carries more
// than one campus (they opened another branch once, or a relocation added it)
// could end up sending campus_id=B while their plan, seats and check-in all
// sit at A, and the portal then contradicts itself: the profile reads the
// server and says "active subscription", while check-in answers "No active
// subscription found for this campus" and the room list comes back empty
// because both are scoped by the campus_id the client sent. So for members the
// server wins. The campus switcher stays authoritative because it POSTs
// select-campus before it touches the client side.
//
// A stored campus that is no longer one of the account's own is dropped rather
// than restored: every scoped endpoint refuses it, so keeping it empties the
// screens instead of falling back to a campus that works.
// ----------------------------------------------------------------------

const STORAGE_KEY = 'activeCampus';

export const readStoredCampus = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch (error) {
    console.error('Error parsing activeCampus from localStorage:', error);
    return null;
  }
};

export const persistActiveCampus = (campus) => {
  if (!campus?.id) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(campus));
  } catch (error) {
    console.error('Error saving activeCampus to localStorage:', error);
  }
};

/** The campus this user should be acting at, or null when they have none. */
export const resolveActiveCampus = (user) => {
  const campuses = user?.userAllCampus || [];
  const match = (id) =>
    id === undefined || id === null
      ? null
      : campuses.find((c) => String(c.id) === String(id)) || null;

  // Members follow the server. Its campus can sit outside the user's own
  // campus list (they never switched to it, so it was never added to them), in
  // which case the payload's own copy of it is what we use.
  if (user?.role === 'user' && user?.activeCampus?.id) {
    return match(user.activeCampus.id) || user.activeCampus;
  }

  if (campuses.length === 1) return campuses[0];

  return match(readStoredCampus()?.id) || campuses[0] || null;
};
